/**
 * @fileoverview Utilities for runtime frontend configuration overrides used by tests.
 *
 * This module implements a cookie-driven override channel for frontend config values
 * (APP, JOBS, RESUME, ORGANIZATIONS, USER, WIZARD) without rebuilding the frontend.
 *
 * How it works:
 * 1. Playwright writes a cookie named `jobiqo_test_frontend_overrides`.
 * 2. The cookie value is URL-encoded JSON (for example:
 *    {"JOBS":{"JOB_SEARCH_AUTO_SELECT_FIRST_JOB":true}}).
 * 3. On SSR, we parse the cookie from the request and deep-merge into base config.
 * 4. On the client, we parse the same cookie and merge again to keep hydration and
 *    client-side navigations consistent with SSR behavior.
 *
 * Safety and scope:
 * - Overrides are ignored in production by default.
 * - Parsing failures are non-fatal; invalid payloads are ignored and logged once.
 * - Merge behavior is recursive for plain objects and replaces arrays/primitives.
 */

import { parse } from 'cookie';
import mergeDeepRight from 'ramda/src/mergeDeepRight';
import type { FrontendConfigContextType } from '../context/frontendConfig';

export const FRONTEND_CONFIG_OVERRIDES_COOKIE =
  'jobiqo_test_frontend_overrides';

let hasWarnedInvalidOverrides = false;

/**
 * Check if a value is a plain object
 * (not null, not an array, and of type 'object').
 *
 * This is used to determine if we should recursively merge or replace values.
 *
 * @param value
 *  The value to check.
 * @returns
 *  True if the value is a plain object, false otherwise.
 */
export const isPlainObject = (value: unknown): value is Record<string, any> => {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === '[object Object]'
  );
};

/**
 * Check if frontend config overrides are enabled based on environment variables.
 *
 * @returns
 *  True if overrides are enabled (inside playwright tests), false otherwise.
 */
const isOverrideEnabled = (): boolean => {
  return process.env.PLAYWRIGHT_TESTING === 'true';
};

/**
 * Log a warning about invalid overrides,
 * but only once per session to avoid spamming the console.
 */
const warnInvalidOverrides = (): void => {
  if (!hasWarnedInvalidOverrides) {
    hasWarnedInvalidOverrides = true;
    // Keep this warning minimal because broken cookies may be user-controlled.
    console.warn(
      'Ignoring invalid frontend config overrides from cookie (non-fatal).',
    );
  }
};

/**
 * Parse the overrides JSON string from the cookie value.
 *
 * This function safely parses the JSON and ensures it is a plain object.
 * If parsing fails or the result is not a plain object, it logs a warning and returns an empty object.
 *
 * @param value
 *  The raw JSON string to parse.
 * @returns
 *  The parsed overrides as a plain object, or an empty object if parsing fails or the result is invalid.
 */
const parseOverridesJSON = (value?: string): Record<string, any> => {
  if (!value) {
    return {};
  }

  try {
    const parsed = JSON.parse(value);
    return isPlainObject(parsed) ? parsed : {};
  } catch (e) {
    // If JSON parsing fails, we log a warning but do not throw,
    // to avoid breaking the app.
    warnInvalidOverrides();
    return {};
  }
};

/**
 * Parse test frontend config overrides from request cookie.
 */
export const parseFrontendOverridesFromReq = (req?: {
  headers?: { cookie?: string };
}): Record<string, any> => {
  if (!isOverrideEnabled() || !req?.headers?.cookie) {
    // If not enabled or not server request
    // with cookies, return empty overrides.
    return {};
  }

  try {
    const cookies = parse(req.headers.cookie);
    const rawValue = cookies[FRONTEND_CONFIG_OVERRIDES_COOKIE];
    if (!rawValue) {
      return {};
    }

    return parseOverridesJSON(decodeURIComponent(rawValue));
  } catch (e) {
    // If an error happens (e.g. malformed cookie header),
    // we log a warning but do not throw, to avoid breaking the server.
    warnInvalidOverrides();
    return {};
  }
};

/**
 * Parse test frontend config overrides from browser cookie.
 */
export const parseFrontendOverridesFromDocumentCookie = (): Record<
  string,
  any
> => {
  if (!isOverrideEnabled()) {
    return {};
  }

  try {
    const rawDocumentCookie = document.cookie || '';
    const cookies = parse(rawDocumentCookie);
    const rawValue = cookies[FRONTEND_CONFIG_OVERRIDES_COOKIE];
    if (!rawValue) {
      return {};
    }

    return parseOverridesJSON(decodeURIComponent(rawValue));
  } catch (e) {
    warnInvalidOverrides();
    return {};
  }
};

/**
 * Deep merge frontend config with overrides.
 *
 * Arrays are replaced (because it's not possible to deep merge arrays),
 * plain objects are merged recursively.
 */
export const mergeFrontendConfig = <T extends FrontendConfigContextType>(
  baseConfig: T,
  overrides: Record<string, any>,
): T => {
  if (!isPlainObject(overrides) || Object.keys(overrides).length === 0) {
    return baseConfig;
  }

  return mergeDeepRight(baseConfig, overrides) as T;
};
